import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { MDSRowItemAppearance } from "@/mds/components/lists/row-item/row-item.types";
import {
  ItemListItemRowData,
  ItemListRowType,
} from "@/components/item-list/types/item-list-row-types";
import { ItemListItemKind } from "@/components/item-list/types/item-list-item-types";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { generateShortDateString } from "@/domains/date/date";
import { MDSIconKind, MDSMenuContentList } from "@/mds";
import { AppStore } from "@/store/AppStore";
import { INoteObservable } from "@/store/note";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { DateTime } from "luxon";

const AWAITING_INPUT_LABEL = "Awaiting Input";

export const getItemListRowForNote = ({
  dateTime,
  dropdown,
  inMainPanel,
  listState,
  noteObservable,
  store,
  trackClick,
  tabId,
  appearance,
  disablePreview = false,
}: {
  dateTime: Date | string;
  dropdown?: MDSMenuContentList;
  inMainPanel: boolean;
  listState?: ListStateObservable;
  noteObservable: INoteObservable;
  store: AppStore;
  trackClick?: () => void;
  tabId?: string;
  appearance: MDSRowItemAppearance;
  disablePreview?: boolean;
}) => {
  const noteId = noteObservable.id;
  const dateLabel = resolveDateLabel({ appearance, dateTime });

  const isLoading = noteObservable.collectionList?.isLoading;
  const collections = noteObservable.collectionList?.allCollections || [];

  const suggestionData = noteObservable.suggestionData;
  const primaryLabel = suggestionData?.suggested_primary_label || noteObservable.title;
  const secondaryLabel =
    suggestionData?.suggested_secondary_label ||
    noteObservable.secondaryTitle ||
    "No additional text";

  const row: ItemListItemRowData = {
    type: ItemListRowType.Item,
    key: noteObservable.id,
    payload: {
      id: noteObservable.id,
      kind: ItemListItemKind.Note,
      titleIcon: noteObservable.isShared ? { kind: MDSIconKind.Shared } : undefined,
      appearance,
      createPreviewState: disablePreview
        ? undefined
        : () =>
            new ItemPreviewState({
              store,
              id: noteId,
              kind: ItemListItemKind.Note,
            }),
      label: primaryLabel,
      onClick: ({ event }) => {
        store.navigation.goToNote({ noteId, event, activeTabId: tabId });
        trackClick?.();
      },
      sharedBy: noteObservable.sharedBy,
      dateLabel,
      dropdown,
      listState,
      chips: () => {
        const sortedCollections = sortCollectionsForChips(collections);
        if (sortedCollections.length === 0) return null;
        return <ItemListRowFeaturedContent inline collections={sortedCollections} />;
      },
      extraRow: {
        id: `note-${noteId}-content`,
        content: () => (
          <ItemListRowFeaturedContent snippet={[{ text: secondaryLabel }]} isLoading={isLoading} />
        ),
      },
    },
  };
  if (!inMainPanel) {
    row.payload.extraRow = {
      id: `note-${noteId}-content`,
      content: () => <ItemListRowFeaturedContent snippet={[{ text: dateLabel }]} />,
    };
    row.payload.onClick = () => {
      store.navigation.goToNote({ noteId });
      trackClick?.();
    };
    row.payload.sharedBy = undefined;
    row.payload.dateLabel = undefined;
    row.payload.dropdown = undefined;
    row.payload.listState = undefined;
  }
  return row;
};

const resolveDateLabel = ({
  appearance,
  dateTime,
}: {
  appearance: MDSRowItemAppearance;
  dateTime: Date | string;
}) => {
  if (appearance === MDSRowItemAppearance.ActiveSuggestion) {
    return AWAITING_INPUT_LABEL;
  }

  let dateLabel = generateShortDateString(
    typeof dateTime === "string" ? DateTime.fromISO(dateTime) : DateTime.fromJSDate(dateTime)
  );

  if (appearance === MDSRowItemAppearance.AudioRecordingUploadingFailed) {
    dateLabel = "Uploading Error";
  }

  if (appearance === MDSRowItemAppearance.AudioRecordingProcessingFailed) {
    dateLabel = "Processing Error";
  }

  if (appearance === MDSRowItemAppearance.AudioRecordingRecording) {
    dateLabel = "Recording";
  }

  if (appearance === MDSRowItemAppearance.AudioRecordingUploading) {
    dateLabel = "Uploading";
  }

  if (appearance === MDSRowItemAppearance.AudioRecordingProcessing) {
    dateLabel = "Processing";
  }

  return dateLabel;
};
